import type { Author } from 'interfaces/content/Authors';
import Link from 'components/Globals/Base/Link';
import type { EventData } from 'services/Gtm/entities';
import type { GridAuthorWithUri } from 'interfaces/content/Ranking';
import { Fragment } from 'react';
import RawHtml from 'utils/miscUtils/rawHtml';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import * as S from './BylineListStyles';
import { COMMON_TEST_IDS } from '../../constants/testsIds/common';

export interface BylineListProps {
  bylineList: Author[] | GridAuthorWithUri[];
  trackingData?: EventData;
  nativoClassName?: string;
  isArticle?: boolean;
  className?: string;
  title?: string;
  description?: string;
  dataCy?: string;
  isHiddenSlide?: boolean;
}

const BylineList = ({
  bylineList,
  isArticle = false,
  className,
  nativoClassName,
  trackingData,
  title,
  description,
  dataCy,
  isHiddenSlide,
}: BylineListProps) => {
  const bylineListLength = bylineList.length;
  const getAuthorUrl = (hrefList: Author[] | GridAuthorWithUri[], index = 0, theTitle = ''): string => {
    const author = !hrefList || index >= hrefList.length ? null : hrefList[index];

    if (!author) {
      return '';
    }

    const baseUrl = author.uri || author.url;
    const normalizedUrl = baseUrl?.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return theTitle === 'Most Popular' ? `${normalizedUrl}/` : normalizedUrl;
  };

  if (bylineListLength === 1) {
    return (
      <S.ListWrapper
        className={className}
        data-cy={dataCy || COMMON_TEST_IDS.LIST_WRAPPER_AUTHORS}
      >
        {nativoClassName ? null : (
          <S.ByWrapper
            className='by-text'
            data-cy={COMMON_TEST_IDS.BY_ELEMENT}
          >
            By{' '}
          </S.ByWrapper>
        )}
        <S.ListItemWrapper
          key={bylineList[0].name}
          className={nativoClassName || 'list-item-wrapper'}
        >
          <Link
            data-cy={COMMON_TEST_IDS.BY_LINE_AUTHOR}
            ariaLabel={`Go to ${bylineList[0].name} author's page`}
            href={getAuthorUrl(bylineList, 0, title)}
            rel='author'
            trackingData={
              trackingData && {
                ...trackingData,
                eventLabel: `${bylineList[0].name} : ${trackingData.eventLabel}`,
              }
            }
            type='secondary'
            tabIndex={isHiddenSlide ? -1 : 0}
            aria-hidden={isHiddenSlide ? 'true' : undefined}
          >
            {bylineList[0].name?.trim()}
          </Link>
        </S.ListItemWrapper>
        {description && <S.DescriptionWrapper>{RawHtml({ html: description })}</S.DescriptionWrapper>}
      </S.ListWrapper>
    );
  }

  if (bylineListLength >= 2 && isArticle) {
    return (
      <S.ListWrapper
        className={className}
        data-cy={dataCy}
      >
        <S.ByWrapper
          className='by-text'
          data-cy={COMMON_TEST_IDS.BY_ELEMENT}
        >
          By{' '}
        </S.ByWrapper>
        {bylineList.map((author: { name: string; uri: string }, idx) => (
          <Fragment key={author.uri}>
            <S.ListItemWrapper key={author.name}>
              <Link
                data-cy={COMMON_TEST_IDS.BY_LINE_AUTHOR}
                tabIndex={isHiddenSlide ? -1 : 0}
                aria-hidden={isHiddenSlide ? 'true' : undefined}
                ariaLabel={`Go to ${author.name} author's page`}
                href={getAuthorUrl(bylineList, idx, title)}
                trackingData={
                  trackingData && {
                    ...trackingData,
                    eventLabel: `${author.name} : ${trackingData.eventLabel}`,
                  }
                }
                type='secondary'
              >
                {author.name?.trim()}
              </Link>
            </S.ListItemWrapper>
            <S.ListItemSeperator>
              {bylineListLength === 2 && idx === 0 && ' and '}
              {bylineListLength > 2 && bylineListLength - 2 > idx && ', '}
              {bylineListLength > 2 && bylineListLength - 2 === idx && ' and '}
            </S.ListItemSeperator>
          </Fragment>
        ))}
        {description && <S.DescriptionWrapper>{RawHtml({ html: description })}</S.DescriptionWrapper>}
      </S.ListWrapper>
    );
  }

  if (bylineListLength >= 2) {
    return (
      <S.ListWrapper
        className={className}
        data-cy={dataCy}
      >
        <S.ByWrapper
          className='by-text'
          data-cy={COMMON_TEST_IDS.BY_ELEMENT}
        >
          By{' '}
        </S.ByWrapper>
        <S.ListItemWrapper
          key={bylineList[0].name}
          className='list-item-wrapper'
        >
          <Link
            data-cy={COMMON_TEST_IDS.BY_LINE_AUTHOR}
            tabIndex={isHiddenSlide ? -1 : 0}
            aria-hidden={isHiddenSlide ? 'true' : undefined}
            ariaLabel={`Go to ${bylineList[0].name} author's page`}
            href={getAuthorUrl(bylineList, 0, title)}
            trackingData={
              trackingData && {
                ...trackingData,
                eventLabel: `${bylineList[0].name} : ${trackingData.eventLabel}`,
              }
            }
            type='secondary'
          >
            {bylineList[0].name?.trim()}
          </Link>
        </S.ListItemWrapper>
        <S.ListItemSeperator className='list-item-seperator'>
          {bylineListLength === 2 ? ' and ' : ', '}
        </S.ListItemSeperator>
        <S.ListItemWrapper
          className='list-item-wrapper'
          key={bylineList[1].name}
        >
          <Link
            data-cy={COMMON_TEST_IDS.BY_LINE_AUTHOR}
            tabIndex={isHiddenSlide ? -1 : 0}
            aria-hidden={isHiddenSlide ? 'true' : undefined}
            ariaLabel={`Go to ${bylineList[1].name} author's page`}
            href={getAuthorUrl(bylineList, 1, title)}
            trackingData={
              trackingData && {
                ...trackingData,
                eventLabel: `${bylineList[1].name} : ${trackingData.eventLabel}`,
              }
            }
            type='secondary'
          >
            {bylineList[1].name?.trim()}
          </Link>
        </S.ListItemWrapper>
        {bylineListLength > 2 && (
          <S.ListItemSeperator className='list-item-seperator'>, and others</S.ListItemSeperator>
        )}
      </S.ListWrapper>
    );
  }

  return null;
};

export default withErrorBoundary(BylineList, { fragment: UIFragments.BYLINE_LIST });
